import { useQuery } from "@tanstack/react-query";

import discountCore from "../../core/discount.core";
import { useTranslation } from "../../core/translation.core";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import HeaderComp from "../common/header.comp";
import { Discount } from "../../types/discount";
import { Tag } from "../reuseables/list.comp";
import PencilIcon from "../../icons/pencil.icon";
import Link from "next/link";
import LocalePs from "../locale/localePs.core";

export default function DiscountsTable() {
  const { t, msg } = useTranslation();

  const { data: discounts, isFetched } = useQuery({
    queryKey: ["scheduleDiscounts"],
    queryFn: discountCore.loadDiscounts,
  });

  if (!isFetched) return <LoadingOvalCenterIcon />;
  if (!discounts) return <ErrorCenterIcon reason={{ message: "null" }} />;

  return (
    <div id="discount-discount-table" className="pt-6 lg:pt-0">
      <div className="hidden lg:grid lg:grid-cols-[auto_150px_150px_150px] gap-6 px-6 py-3 text-sm">
        <HeaderComp>{t(msg.addOnDiscount.name)}</HeaderComp>
      </div>
      {discounts.map((discount, index) => (
        <DiscountRow key={index} discount={discount} />
      ))}
    </div>
  );
}

interface DiscountRowProps {
  discount: Discount;
}

function DiscountRow({ discount }: DiscountRowProps) {
  const { t, msg } = useTranslation();

  return (
    <div
      className="offer-row grid grid-cols-[1fr_1fr_50px] gap-x-6 gap-y-3 px-6 py-6 border-t-[1px] text-sm"
      data-offer-name={discount.name}
    >
      <div>
        <Tag>{t(msg.addOnDiscount.name)}</Tag>
        {discount.name}
      </div>

      <div className="justify-self-start">
        <Items discount={discount} />
      </div>

      <div className="mt-4 xl:mt-0 justify-self-start">
        <Link href={`/scheduleDiscount/edit/?id=${discount.id}`} className="edit-link">
          <PencilIcon className="w-4 h-4 text-her-robinEggBlue" />
        </Link>
      </div>
    </div>
  );
}

function Items({ discount }: { discount: Discount }) {
  if (!discount) return null;

  if (discount.scheduleDiscountKind === "buyXGetY%Off" && discount.buyXGetYPercentOff?.tiers?.length) {
    return (
      <div data-schedule-buyx-summary className="text-xs text-slate-600 dark:text-slate-300">
        {discount.buyXGetYPercentOff.tiers.map((tier, i) => (
          <span key={i} className="mr-2">
            ≥{tier.minBuyQty}: {tier.discountPercentage}%
          </span>
        ))}
        {discount.buyXGetYPercentOff.maxDiscountedUnits != null ? (
          <span>cap {discount.buyXGetYPercentOff.maxDiscountedUnits}</span>
        ) : null}
      </div>
    );
  }

  if (!discount.discountItems || discount.discountItems.length < 1) return null;

  return (
    <table className="border-collapse border border-slate-500">
      <tbody>
        {discount?.discountItems.map((p) => (
          <tr key={p?.product?.id}>
            <Td>
              <LocalePs value={p?.product?.name || p?.category?.name} />
            </Td>
            <Td>
              {p?.product?.sku}
              {p?.category?.code}
            </Td>
            {p.discountPrice && <Td>${p?.discountPrice}</Td>}
            {p.discountPercentage && <Td>{p?.discountPercentage}%</Td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="border border-slate-700 px-2">{children}</td>;
}
